import { toRefs, reactive, computed } from 'vue';
import { fixEmissionPointProblem as _fixEmissionPointProblem } from '../common/functions/emissionPointManager';
import { queryClient } from '../api/queryClient';
import clientState from '../common/states/clientState';
import supplierState from '../common/states/supplierState';
import productState from '../common/states/productState';
import taxState from '../common/states/taxState';
import supportCodeState from '../common/states/supportCodeState';
import type { companyUserStates as CompanyUserStatesType } from '../common/models/states.interface';
import type { CompanyRoleModulePermission, SystemRole } from '../types/permissions';

const companyUserStates: CompanyUserStatesType = reactive({
  branch: null,
  emisionPoint: null,
  lastCleared: 0,
  showConfigCompanyData: 0,
  showConfigCompanyProducts: 0,
  hasProducts: false,
  hasEmissionPoints: false,
  activeTabIndex: 0,
});

const userState = reactive({
  data: null,
});

const dynamicPermissionsState = reactive<{
  permissions: CompanyRoleModulePermission[];
  activeModules: string[];
  currentRole: SystemRole | null;
  isLoaded: boolean;
}>({
  permissions: [],
  activeModules: [],
  currentRole: null,
  isLoaded: false,
});

const spinnerState = reactive({
  showSpinner: false,
});

export function useStates() {
  const loadDynamicPermissions = async (companyId: number, roleId: number) => {
    try {
      const { permissionService } = await import('../services/permissionService');
      const permissions = await permissionService.getCompanyRolePermissions(companyId, roleId);
      
      dynamicPermissionsState.permissions = permissions;
      dynamicPermissionsState.isLoaded = true;
    } catch (error) {
      console.error('[States] Error cargando permisos dinámicos:', error);
      dynamicPermissionsState.permissions = [];
      dynamicPermissionsState.isLoaded = false;
    }
  };

  const loadActiveModules = (data: any) => {
    if (data && data.allowedModules) {
      dynamicPermissionsState.activeModules = data.allowedModules;
    } else {
      dynamicPermissionsState.activeModules = [];
    }
  };

  const loadCurrentRole = async (roleId: number) => {
    try {
      const { systemRoleService } = await import('../services/systemRoleService');
      const role = await systemRoleService.getRoleById(roleId);
      dynamicPermissionsState.currentRole = role;
    } catch (error) {
      console.error('Error loading current role:', error);
      dynamicPermissionsState.currentRole = null;
    }
  };

  const onSetInitialData = async (data: any) => {
    userState.data = data;

    // Guardar allowedModules en sessionStorage para acceso rápido
    if (data && data.allowedModules) {
      sessionStorage.setItem('allowedModules', JSON.stringify(data.allowedModules));
    }

    // Cargar permisos dinámicos desde BD
    if (data && data.companyId && data.systemRoleId) {
      loadActiveModules(data);
      await loadCurrentRole(data.systemRoleId);
      await loadDynamicPermissions(data.companyId, data.systemRoleId);
    }
  };

  const onLogout = () => {
    // Limpiar el estado del usuario
    userState.data = null;

    // Limpiar allowedModules del sessionStorage
    sessionStorage.removeItem('allowedModules');

    // Limpiar permisos dinámicos
    dynamicPermissionsState.permissions = [];
    dynamicPermissionsState.activeModules = [];
    dynamicPermissionsState.currentRole = null;
    dynamicPermissionsState.isLoaded = false;

    // Limpiar el estado de clientes, proveedores, productos y taxes
    clientState.clearClientData();
    supplierState.clearSupplierData();
    productState.clearProductData();
    taxState.clearTaxesData();
    supportCodeState.clearSupportCodesData();

    // Limpiar el estado de redirecciones directamente
    companyUserStates.hasProducts = false;

    // Invalidar toda la caché de Vue Query
    queryClient.clear();
  };

  const onShowSpinner = () => {
    spinnerState.showSpinner = true;
  };

  const onHideSpinner = () => {
    spinnerState.showSpinner = false;
  };

  const onSetEmissionPoint = (data) => {
    // Crear nuevos objetos para asegurar reactividad completa
    companyUserStates.branch = { ...data.branch };
    companyUserStates.emisionPoint = { ...data.emissionPoint };

    // Ya no actualizamos userState.data para evitar efectos secundarios
    // Los componentes que necesiten estos valores deben usar companyUserStates
  };

  const onClearEmissionPoint = () => {
    companyUserStates.branch = null as any;
    companyUserStates.emisionPoint = null as any;
    companyUserStates.lastCleared = Date.now();

    // Ya no actualizamos userState.data para evitar efectos secundarios
  };

  const onShowConfigCompanyData = () => {
    companyUserStates.showConfigCompanyData = Date.now();
  };

  const onShowConfigCompanyProducts = () => {
    companyUserStates.showConfigCompanyProducts = Date.now();
  };

  const onSetHasProducts = (hasProducts) => {
    companyUserStates.hasProducts = hasProducts;
  };

  const onSetHasEmissionPoints = (hasEmissionPoints) => {
    companyUserStates.hasEmissionPoints = hasEmissionPoints;
  };

  const selectBranchesTab = () => {
    companyUserStates.activeTabIndex = 1;
  };

  // Refresca los datos del punto de emisión desde el backend (sin limpiar la selección actual)
  // Esto dispara el watcher de isEmissionPointLoad en AppTopbarMenu → re-fetch de branches y EP
  const refreshEmissionPointData = () => {
    companyUserStates.lastCleared = Date.now();
  };

  // Esta función es un wrapper que pasa el estado de la empresa a la función compartida
  const fixEmissionPointProblem = (options) => {
    _fixEmissionPointProblem(options, companyUserStates);
  };

  const isUserLoad = computed(() => !!userState.data);

  const isEmissionPointLoad = computed(() => {
    return companyUserStates.lastCleared;
  });

  /**
   * Indica si la compañía está autorizada como Agente de Retención (SRI).
   * Si false: ocultar UI de emisión de retenciones (menús, rutas, columnas, botones).
   * También se considera false si el dropdown del certificado está en "NA" (NOT_APPLICABLE).
   */
  const isWithholdingAgent = computed<boolean>(() => {
    const data: any = userState.data;
    if (!data) return false;
    if (data.isWithholdingAgent === false) return false;
    if (data.isWithholdingAgent === true) return true;
    return false;
  });

  /**
   * Actualiza el flag isWithholdingAgent en caliente (ej: tras guardar Información Tributaria).
   * Evita tener que recargar la sesión completa.
   */
  const onSetWithholdingAgent = (value: boolean) => {
    if (userState.data) {
      (userState.data as any).isWithholdingAgent = !!value;
    }
  };

  const showConfigCompanyData = computed(() => {
    return companyUserStates.showConfigCompanyData;
  });

  const showConfigCompanyProducts = computed(() => {
    return companyUserStates.showConfigCompanyProducts;
  });

  return {
    userState: toRefs(userState),
    spinnerState: toRefs(spinnerState),
    dynamicPermissionsState: toRefs(dynamicPermissionsState),
    companyUserStates,
    isUserLoad,
    isEmissionPointLoad,
    isWithholdingAgent,
    showConfigCompanyData,
    showConfigCompanyProducts,
    onSetHasProducts,
    onSetHasEmissionPoints,
    onSetInitialData,
    onSetWithholdingAgent,
    onLogout,
    onShowSpinner,
    onHideSpinner,
    onSetEmissionPoint,
    onClearEmissionPoint,
    onShowConfigCompanyData,
    onShowConfigCompanyProducts,
    selectBranchesTab,
    fixEmissionPointProblem,
    refreshEmissionPointData,
    loadDynamicPermissions,
    loadActiveModules,
    loadCurrentRole,
  };
}
